import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { addPreJudgement } from "services/caseServices";
import {
  CaseCreation,
  CaseDefendantLink,
  CountDefendantsList,
  PreJudgement,
  PreJudgementDecision,
  PreJudgementDecisionForSelect,
} from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  caseData: CaseCreation;
  onClose: (refetch: boolean) => void;
};

const PreJugmentForm: FC<Props> = ({ caseData, onClose }) => {
  const { showTooltip } = useTooltip();

  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<PreJudgement>({});
  const [countIndex, setCountIndex] = useState<number>();
  const [defendantId, setDefendantId] = useState<any>();
  const [countDefendant, setCountDefendant] = useState<any>();
  const [hasAlternate, setHasAlternate] = useState(false);
  const [hearingType, setHearingType] = useState<
    | ""
    | "terminated_due_to_absence_of_defendant"
    | "considered_as_not_present"
    | "case_deferred_temporarily"
  >("");
  const [preliminaryObligation, setPreliminaryObligation] = useState<
    "" | "termination" | "rejection"
  >("");
  const [rejectionType, setRejectionType] = useState<
    "sworn" | "refused_to_testify"
  >("sworn");

  const [countDefendantList, setCountDefendantList] = useState<
    {
      defendant: CaseDefendantLink;
      count: CountDefendantsList;
      has_alternate: boolean;
    }[]
  >();

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...(prevState ?? {}),
      [name]: name === "decision_date" ? value : value,
    }));
  };

  const validateForm = () => {
    if (hearingType) {
      return "";
    }
    if (!formState?.decision_date) {
      return "Decision date is required.";
    }
    if (!formState?.summary) {
      return "Summary is required.";
    }
    if (!formState?.decision) {
      return "Decision is required.";
    }
    if (!formState?.remarks) {
      return "Remarks are required.";
    }
    if (!defendantId) {
      return "Defendant is required.";
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }
    setLoading(true);
    const data = {
      ...formState,
      decision: hearingType || formState?.decision,
      case: caseData.id,
      opening_case_file: caseData?.opening_case_files?.[0]?.id || null,
      defendant: defendantId,
      count_defendant: countDefendant,
      has_alternate: hasAlternate,
      is_sworn:
        preliminaryObligation === "rejection" && rejectionType === "sworn",
      is_refused_to_testify:
        preliminaryObligation === "rejection" &&
        rejectionType === "refused_to_testify",
    };
    addPreJudgement(data)
      .then(() => {
        // if (checkboxState.isCaseUpdate || checkboxState.isCaseTerminated) {
        //   editCaseCreation({
        //     id: caseData?.id,
        //     status: checkboxState.isCaseUpdate
        //       ? "case_on_updated"
        //       : "case_terminated_by_court",
        //     old_status: caseData?.status,
        //   });
        // }

        // if (checkboxState.isCaseTerminated && state?.id) {
        //   editReceivingInvestigationFile2({
        //     id: state?.id,
        //     status: "case_terminated_by_court",
        //   });
        // }

        // if (checkboxState.isCaseOutOfJurisdiction) {
        //   editCaseCreation({
        //     id: caseData?.id,
        //     status: "out_of_jurisdiction",
        //     old_status: caseData?.status,
        //     out_of_jursdicition_source: jurisdictionSource,
        //   });
        //   editReceivingInvestigationFile2({
        //     id: state?.id,
        //     status: "out_of_jurisdiction",
        //     out_of_jursdicition_source: jurisdictionSource,
        //   });
        // }

        showTooltip("Successfully Added!", "success");
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };
  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  useEffect(() => {
    if (caseData?.counts && caseData?.PreJudgementEntry) {
      const data = caseData?.counts
        ?.filter((c) => !c?.is_article_of_cahrge_changed)
        ?.flatMap((c) => {
          const base = (c?.defendants_info || []).map((d: any) => ({
            defendant: d,
            count: c,
            has_alternate: false,
          }));
          if (c?.has_alternate) {
            const alternate = (c?.defendants_info || []).map((d: any) => ({
              defendant: d,
              count: c,
              has_alternate: true,
            }));
            return [...base, ...alternate];
          }
          return base;
        });

      // Filter out already used defendant+count+has_alternate pairs
      let filtered = data.filter((a) => {
        return !caseData.PreJudgementEntry?.some(
          (f) =>
            f?.defendant_info?.id === a?.defendant?.id &&
            f?.count_defendant_info?.id === a?.count?.id &&
            Boolean(f?.has_alternate) === Boolean(a?.has_alternate),
        );
      });

      setCountDefendantList(filtered);
    }
  }, [caseData]);

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">Pre-Judgement Form</h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          Fill in the pre-judgement details below.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Hearing Type
              </label>
              <div className="space-y-2">
                <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                  <input
                    type="radio"
                    name="hearingType"
                    value="terminated_due_to_absence_of_defendant"
                    checked={
                      hearingType === "terminated_due_to_absence_of_defendant"
                    }
                    onChange={(e) => {
                      const value = e.target.value as
                        | "terminated_due_to_absence_of_defendant"
                        | "considered_as_not_present"
                        | "case_deferred_temporarily";
                      setHearingType(value);
                      setPreliminaryObligation("");
                      setFormState((prev) => ({
                        ...(prev ?? {}),
                        decision: value,
                      }));
                    }}
                    className="mt-1 text-red-600 focus:ring-red-500"
                  />
                  <div className="ml-3 text-sm text-gray-900 font-medium">
                    Terminated due to Absence of Defendant
                  </div>
                </label>

                <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                  <input
                    type="radio"
                    name="hearingType"
                    value="considered_as_not_present"
                    checked={hearingType === "considered_as_not_present"}
                    onChange={(e) => {
                      const value = e.target.value as
                        | "terminated_due_to_absence_of_defendant"
                        | "considered_as_not_present"
                        | "case_deferred_temporarily";
                      setHearingType(value);
                      setPreliminaryObligation("");
                      setFormState((prev) => ({
                        ...(prev ?? {}),
                        decision: value,
                      }));
                    }}
                    className="mt-1 text-red-600 focus:ring-red-500"
                  />
                  <div className="ml-3 text-sm text-gray-900 font-medium">
                    Considered as Not Present
                  </div>
                </label>

                <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                  <input
                    type="radio"
                    name="hearingType"
                    value="case_deferred_temporarily"
                    checked={hearingType === "case_deferred_temporarily"}
                    onChange={(e) => {
                      const value = e.target.value as
                        | "terminated_due_to_absence_of_defendant"
                        | "considered_as_not_present"
                        | "case_deferred_temporarily";
                      setHearingType(value);
                      setPreliminaryObligation("");
                      setFormState((prev) => ({
                        ...(prev ?? {}),
                        decision: value,
                      }));
                    }}
                    className="mt-1 text-red-600 focus:ring-red-500"
                  />
                  <div className="ml-3 text-sm text-gray-900 font-medium">
                    Case Deferred Temporarily
                  </div>
                </label>
              </div>
              {formState?.decision && (
                <div
                  className="text-sm text-red-500 cursor-pointer mt-2"
                  onClick={() => {
                    setFormState((prev) => ({
                      ...(prev ?? {}),
                      decision: null,
                    }));
                  }}
                >
                  Remove Decision
                </div>
              )}
            </div>

            {!hearingType && (
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  Preliminary Obligations
                </label>
                <div className="space-y-2">
                  <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                    <input
                      type="radio"
                      name="preliminaryObligation"
                      value="termination"
                      checked={preliminaryObligation === "termination"}
                      onChange={(e) => {
                        setPreliminaryObligation(e.target.value as any);
                        setHearingType("");
                      }}
                      className="mt-1 text-red-600 focus:ring-red-500"
                    />
                    <div className="ml-3 text-sm text-gray-900 font-medium">
                      Termination
                    </div>
                  </label>

                  <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                    <input
                      type="radio"
                      name="preliminaryObligation"
                      value="rejection"
                      checked={preliminaryObligation === "rejection"}
                      onChange={(e) => {
                        setPreliminaryObligation(e.target.value as any);
                        setHearingType("");
                      }}
                      className="mt-1 text-red-600 focus:ring-red-500"
                    />
                    <div className="ml-3 text-sm text-gray-900 font-medium">
                      Rejection
                    </div>
                  </label>

                  {preliminaryObligation === "rejection" && (
                    <div className="ml-8 space-y-2">
                      <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                        <input
                          type="radio"
                          name="rejectionType"
                          value="sworn"
                          checked={rejectionType === "sworn"}
                          onChange={(e) => {
                            setRejectionType(e.target.value as any);
                          }}
                          className="mt-1 text-red-600 focus:ring-red-500"
                        />
                        <div className="ml-3 text-sm text-gray-900 font-medium">
                          Sworn
                        </div>
                      </label>

                      <label className="flex items-start p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors has-[:checked]:border-red-500 has-[:checked]:bg-red-50">
                        <input
                          type="radio"
                          name="rejectionType"
                          value="refused_to_testify"
                          checked={rejectionType === "refused_to_testify"}
                          onChange={(e) => {
                            setRejectionType(e.target.value as any);
                          }}
                          className="mt-1 text-red-600 focus:ring-red-500"
                        />
                        <div className="ml-3 text-sm text-gray-900 font-medium">
                          Refused to Testify
                        </div>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {!hearingType && (
              <>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Select Count Defendant
                  </label>
                  <select
                    className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
                    value={countIndex}
                    onChange={(e) => {
                      const index = Number(e.target.value);
                      const value = countDefendantList
                        ? countDefendantList[index]
                        : null;
                      console.log(value);
                      setCountIndex(index);
                      setDefendantId(value?.defendant?.id);
                      setCountDefendant(value?.count?.id);
                      setHasAlternate(value?.has_alternate || false);
                    }}
                  >
                    <option value={""}>Select Defendant</option>
                    {countDefendantList?.map((def: any, idx: number) => (
                      <option key={def.id} value={idx}>
                        {def?.defendant.defendant_info?.first_name}{" "}
                        {def?.defendant.defendant_info?.middle_name}{" "}
                        {def?.defendant.defendant_info?.last_name} :{" "}
                        {def.count?.count_number}{" "}
                        {def?.has_alternate ? "(Alternate)" : ""}
                      </option>
                    ))}
                  </select>
                </div>
                {/* <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Defendant
              </label>

              <select
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="defendant"
                value={
                  formState?.defendant
                    ? typeof formState.defendant === "object"
                      ? formState.defendant.id
                      : formState.defendant
                    : ""
                }
                onChange={handleChange}
              >
                <option value="">Select Defendant</option>
                {caseData?.case_defendant_links
                  ?.filter(
                    (def: any) =>
                      !caseData?.PreJudgementEntry?.some(
                        (entry: any) => entry?.defendant_info?.id === def?.id
                      )
                  )
                  .map((def: any) => (
                    <option key={def.id} value={def.id}>
                      Defendant {def.defendant_number} :{" "}
                      {def.defendant_info?.first_name}{" "}
                      {def.defendant_info?.middle_name}{" "}
                      {def.defendant_info?.last_name}
                    </option>
                  ))}
              </select>
            </div> */}
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Decision Date
                  </label>
                  <input
                    type="date"
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="decision_date"
                    value={
                      formState?.decision_date
                        ? new Date(formState.decision_date)
                            .toISOString()
                            .substring(0, 10)
                        : ""
                    }
                    onChange={handleChange}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Summary
                  </label>
                  <textarea
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="summary"
                    value={formState?.summary ?? ""}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Decision
                  </label>
                  <select
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="decision"
                    value={formState?.decision ?? ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Decision</option>
                    {Object.entries(PreJudgementDecisionForSelect).map(
                      ([key, value]) => (
                        <option key={key} value={key}>
                          {value}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <div className="flex flex-col mt-4">
                  <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                    Remarks
                  </label>
                  <textarea
                    className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                    name="remarks"
                    value={formState?.remarks ?? ""}
                    onChange={handleChange}
                    rows={4}
                  />
                </div>
              </>
            )}
            {/* <label className="flex items-center mt-4 gap-2 text-sm text-gray-600 font-[inter] font-[400] mb-2">
              <input
                type="checkbox"
                className="bg-[#F2F2F2] rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                checked={checkboxState.isCaseUpdate}
                onChange={() => {
                  setCheckboxState({
                    isCaseUpdate: !checkboxState.isCaseUpdate,
                    isCaseTerminated: false,
                    isCaseOutOfJurisdiction: false,
                  });
                }}
              />
              Is case need to be updated?
            </label>
            <label className="flex items-center mt-4 gap-2 text-sm text-gray-600 font-[inter] font-[400] mb-2">
              <input
                type="checkbox"
                className="bg-[#F2F2F2] rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                checked={checkboxState.isCaseTerminated}
                onChange={() => {
                  setCheckboxState({
                    isCaseUpdate: false,
                    isCaseTerminated: !checkboxState.isCaseTerminated,
                    isCaseOutOfJurisdiction: false,
                  });
                }}
              />
              Is case terminated by court?
            </label>
            <label className="flex items-center mt-4 gap-2 text-sm text-gray-600 font-[inter] font-[400] mb-2">
              <input
                type="checkbox"
                className="bg-[#F2F2F2] rounded-lg focus:outline-none focus:ring focus:ring-blue-300"
                checked={checkboxState.isCaseOutOfJurisdiction}
                onChange={() => {
                  setCheckboxState({
                    isCaseUpdate: false,
                    isCaseTerminated: false,
                    isCaseOutOfJurisdiction:
                      !checkboxState.isCaseOutOfJurisdiction,
                  });
                  setJurisdictionSource("");
                }}
              />
              Is case out of jurisdiction?
            </label> */}
            {/* {checkboxState.isCaseOutOfJurisdiction && (
              <div className="flex flex-col mt-4">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  If out of jurisdiction, specify the source
                </label>
                <textarea
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  value={jurisdictionSource}
                  onChange={(e) => setJurisdictionSource(e.target.value)}
                  rows={4}
                />
              </div>
            )} */}

            {/* <div className="flex flex-col mt-4">
              <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                Opening Case File
              </label>
              <select
                className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                name="opening_case_file"
                value={
                  formState?.opening_case_file
                    ? String(
                        (formState.opening_case_file as OpeniningCaseFile).id
                      )
                    : ""
                }
                onChange={(e) => {
                  const selectedId = Number(e.target.value);
                  const selectedFile = caseData?.opening_case_files?.find(
                    (f: any) => f.id === selectedId
                  );
                  setFormState((prev) => ({
                    ...prev,
                    opening_case_file: selectedFile,
                  }));
                }}
              >
                <option value="">Select Opening Case File</option>
                {caseData?.opening_case_files?.map((file: any) => (
                  <option key={file.id} value={file.id}>
                    {file.court_registration_number || file.id}
                  </option>
                ))}
              </select>
            </div> */}
          </div>
          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                      text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                              ${
                                loading ? "opacity-50 cursor-not-allowed" : ""
                              }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreJugmentForm;
